/**
 * Help Center → FAQs tab (FR-HELP-03, AC-HELP-003).
 *
 * Search and the persona scope are server-side (`GET /help/v1/faqs`), debounced
 * so a keystroke does not fire a request per character. The "all roles" toggle
 * drops the persona filter entirely — a coach can still find the answer an
 * operator was given.
 *
 * State lives in the URL (`?help_q=`, `?help_roles=all`, `?help_page=`) so the
 * overlay survives a browser refresh and a link to a search is shareable.
 *
 * If the help service cannot be reached the tab degrades to the copy bundled
 * with the app ({@link FALLBACK_FAQS}) filtered in the browser, rather than
 * showing an empty panel at kickoff.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { MarkdownView } from '../../components/markdown-view';
import { TextField } from '../../components/text-field';
import { ApiError } from '../../lib/api-error';
import type { FaqEntry } from '../../lib/api-types';
import { FALLBACK_FAQS } from '../../lib/help-content';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 250;

type LoadState = 'loading' | 'ready' | 'fallback';

interface FaqResult {
  items: readonly FaqEntry[];
  total: number;
  state: LoadState;
  error: string | null;
}

const EMPTY: FaqResult = { items: [], total: 0, state: 'loading', error: null };

function filterBundled(term: string, persona: Persona, isAllRoles: boolean): FaqEntry[] {
  const needle = term.trim().toLowerCase();
  return FALLBACK_FAQS.filter((entry) => {
    const matchesPersona =
      isAllRoles || entry.persona_tags.length === 0 || entry.persona_tags.includes(persona);
    const matchesTerm =
      needle === '' || `${entry.question} ${entry.answer_markdown}`.toLowerCase().includes(needle);
    return matchesPersona && matchesTerm;
  });
}

export function FaqTab({ persona }: { persona: Persona }): JSX.Element {
  const apiClient = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const term = searchParams.get('help_q') ?? '';
  const isAllRoles = searchParams.get('help_roles') === 'all';
  const page = Math.max(1, Number.parseInt(searchParams.get('help_page') ?? '1', 10) || 1);

  // Local mirror so typing stays responsive while the URL updates on a debounce.
  const [draft, setDraft] = useState(term);
  const [result, setResult] = useState<FaqResult>(EMPTY);
  const isFirstRender = useRef(true);

  const patchParams = useCallback(
    (changes: Record<string, string | null>): void => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Debounce the URL write; the fetch below keys off the committed term.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    if (draft === term) return undefined;
    const timer = setTimeout(
      () => patchParams({ help_q: draft, help_page: null }),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [draft, patchParams, term]);

  useEffect(() => {
    const controller = new AbortController();
    setResult((current) => ({ ...current, state: 'loading', error: null }));

    apiClient
      .listFaqs(
        {
          persona,
          all_roles: isAllRoles,
          q: term,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal
      )
      .then((response) => {
        // Defensive: an unexpected payload shape degrades to "no answers"
        // rather than crashing the overlay a coach opened mid-game.
        const items = Array.isArray(response?.items) ? response.items : [];
        setResult({
          items,
          total: typeof response?.total === 'number' ? response.total : items.length,
          state: 'ready',
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const bundled = filterBundled(term, persona, isAllRoles);
        setResult({
          items: bundled,
          total: bundled.length,
          state: 'fallback',
          error: ApiError.isApiError(error)
            ? error.message
            : 'Live help content could not be reached.',
        });
      });

    return () => controller.abort();
  }, [apiClient, isAllRoles, page, persona, term]);

  const lastPage = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  const rangeLabel = useMemo(() => {
    if (result.total === 0) return 'No answers';
    const first = (page - 1) * PAGE_SIZE + 1;
    const last = Math.min(result.total, first + result.items.length - 1);
    return `Showing ${first}–${last} of ${result.total}`;
  }, [page, result.items.length, result.total]);

  return (
    <div className="stack help-faq">
      <div className="help-faq__controls">
        <TextField
          label="Search FAQs"
          type="search"
          value={draft}
          placeholder="conflict, Hudl import, trial, offline…"
          onChange={(event) => setDraft(event.target.value)}
        />
        <label className="help-faq__toggle" htmlFor="faq-all-roles">
          <input
            id="faq-all-roles"
            type="checkbox"
            checked={isAllRoles}
            onChange={(event) =>
              patchParams({
                help_roles: event.target.checked ? 'all' : null,
                help_page: null,
              })
            }
          />
          <span>
            Show answers for all roles
            <span className="field__hint">
              {isAllRoles
                ? 'Showing every published answer.'
                : `Scoped to ${PERSONA_LABELS[persona]}.`}
            </span>
          </span>
        </label>
      </div>

      {result.state === 'fallback' ? (
        <div className="banner banner--warning" role="status">
          <span className="banner__title">Offline answers</span>
          <span>{result.error} Showing the answers bundled with the app.</span>
        </div>
      ) : null}

      {result.state === 'loading' ? (
        <p className="muted" role="status">
          Loading answers…
        </p>
      ) : null}

      {result.state !== 'loading' && result.items.length === 0 ? (
        <div className="help-empty" role="status">
          <p>
            No FAQ matches{term === '' ? ' your filters' : ` “${term}”`}.
            {isAllRoles ? '' : ' Try “Show answers for all roles”,'} check the Docs tab, or send the
            question to support.
          </p>
        </div>
      ) : null}

      <ul className="help-list">
        {result.items.map((entry) => (
          <li key={entry.id}>
            <details>
              <summary>
                {entry.question}
                {entry.scope === 'tenant' ? <span className="badge">Your team</span> : null}
              </summary>
              <MarkdownView source={entry.answer_markdown} highlight={term} />
              {entry.persona_tags.length > 0 ? (
                <p className="help-list__personas">
                  For: {entry.persona_tags.map((tag) => tag.replace(/_/g, ' ')).join(', ')}
                </p>
              ) : null}
            </details>
          </li>
        ))}
      </ul>

      {result.total > PAGE_SIZE ? (
        <nav className="help-pager" aria-label="FAQ pages">
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={page <= 1}
            onClick={() => patchParams({ help_page: String(page - 1) })}
          >
            Previous
          </button>
          <span className="muted">{rangeLabel}</span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={page >= lastPage}
            onClick={() => patchParams({ help_page: String(page + 1) })}
          >
            Next
          </button>
        </nav>
      ) : (
        <p className="muted">{rangeLabel}</p>
      )}
    </div>
  );
}
